/**
 * ``useSelfCancel`` — drives the order self-cancel control + live countdown (ТЗ §11).
 *
 * The server is authoritative: it returns ``can_self_cancel`` and ``self_cancel_seconds_left``
 * on every order payload. This hook ticks the remaining seconds down locally for a smooth
 * timer, exposes whether the «Отменить заказ» button is still actionable, and posts the
 * cancellation (``POST /api/orders/<id>/cancel/``) — re-validated and refunded server-side
 * (ТЗ §12). Once the window elapses the button gives way to the «недоступна» hint.
 */
import { ApiError } from "@shared/api";
import { cancelOrder, isSelfCancelStage, type OrderStatus } from "@entities/order";
import { useCallback, useEffect, useRef, useState } from "react";

export interface UseSelfCancelInput {
  readonly orderId: number;
  readonly status: OrderStatus;
  /** Server-computed: the window is still open. */
  readonly canSelfCancel: boolean;
  /** Server-computed seconds remaining in the window. */
  readonly secondsLeft: number;
  /** Called after a successful cancel so the screen can refetch the order. */
  readonly onCancelled: () => void;
}

export interface SelfCancelState {
  /** Live seconds remaining (counts down locally). */
  readonly secondsLeft: number;
  /** The button is actionable right now. */
  readonly isAvailable: boolean;
  /** The window has elapsed for an order that could have been self-cancelled. */
  readonly isExpired: boolean;
  readonly isCancelling: boolean;
  readonly error: ApiError | null;
  readonly cancel: () => void;
}

export function useSelfCancel({
  orderId,
  status,
  canSelfCancel,
  secondsLeft,
  onCancelled,
}: UseSelfCancelInput): SelfCancelState {
  const stage = isSelfCancelStage(status);
  const [left, setLeft] = useState(secondsLeft);
  const [isCancelling, setIsCancelling] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  // Re-sync whenever the server value changes (a refetch resets the countdown baseline).
  useEffect(() => {
    setLeft(secondsLeft);
  }, [secondsLeft]);

  const running = stage && canSelfCancel && secondsLeft > 0;
  useEffect(() => {
    if (!running) {
      return undefined;
    }
    const id = window.setInterval(() => {
      setLeft((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => {
      window.clearInterval(id);
    };
  }, [running, secondsLeft]);

  const onCancelledRef = useRef(onCancelled);
  onCancelledRef.current = onCancelled;

  const isAvailable = stage && canSelfCancel && left > 0 && !isCancelling;
  const isExpired = stage && !isAvailable && !isCancelling;

  const cancel = useCallback((): void => {
    setIsCancelling(true);
    setError(null);
    cancelOrder(orderId)
      .then(() => {
        onCancelledRef.current();
      })
      .catch((err: unknown) => {
        setError(
          err instanceof ApiError ? err : new ApiError(0, "Network error", err),
        );
      })
      .finally(() => {
        setIsCancelling(false);
      });
  }, [orderId]);

  return { secondsLeft: left, isAvailable, isExpired, isCancelling, error, cancel };
}
